import { useState } from 'react'
import { Save, RotateCcw, Eye, Palette, Terminal } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { ThemeConfig } from '@/types/theme'
import { AppearanceIdentidadeTab } from '@/components/Configuracao/AppearanceIdentidadeTab'
import { AppearanceCoresTab } from '@/components/Configuracao/AppearanceCoresTab'
import { AppearanceTypographyTab } from '@/components/Configuracao/AppearanceTypographyTab'

export type AppearanceTabProps = {
  draft: ThemeConfig
  updateDraft: (partial: Partial<ThemeConfig>) => void
  setTheme: (partial: Partial<ThemeConfig>) => void
  handleSave: () => void
  handleReset: () => void
  isSaving: boolean
}

export function AppearanceTab({
  draft,
  updateDraft,
  setTheme,
  handleSave,
  handleReset,
  isSaving,
}: AppearanceTabProps) {
  const [subTab, setSubTab] = useState<'identity' | 'colors' | 'typography'>('identity')

  const subTabs = [
    { id: 'identity' as const, label: 'Identidade', icon: <Eye size={13} /> },
    { id: 'colors' as const, label: 'Cores', icon: <Palette size={13} /> },
    { id: 'typography' as const, label: 'Tipografia', icon: <Terminal size={13} /> },
  ]

  return (
    <div className="space-y-4">
      <div className="glass-card p-5 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-1 p-1 bg-white/[0.04] rounded-lg border border-white/[0.06] w-fit">
            {subTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSubTab(tab.id)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold whitespace-nowrap transition-all min-h-[44px]',
                  subTab === tab.id
                    ? 'bg-accent text-white shadow-lg shadow-accent/20'
                    : 'text-text-muted hover:text-text-primary hover:bg-white/[0.06]',
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold text-text-muted hover:text-text-primary hover:bg-white/[0.06] transition-all"
            >
              <RotateCcw size={14} />
              Restaurar Padrão
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold bg-accent text-white shadow-lg shadow-accent/20 hover:bg-accent/90 transition-all',
                isSaving && 'opacity-70 cursor-not-allowed',
              )}
            >
              <Save size={14} />
              {isSaving ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </div>
      </div>

      {subTab === 'identity' && <AppearanceIdentidadeTab draft={draft} updateDraft={updateDraft} />}
      {subTab === 'colors' && (
        <AppearanceCoresTab draft={draft} updateDraft={updateDraft} setTheme={setTheme} />
      )}
      {subTab === 'typography' && <AppearanceTypographyTab draft={draft} updateDraft={updateDraft} />}
    </div>
  )
}
